import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Image as ImageIcon, Plus, Trash2, Upload, Globe2, Home as HomeIcon, Loader2, ExternalLink } from "lucide-react";
import { adminAxios } from "../AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Load Pannellum lazily via CDN when the 360 modal opens.
let PANNELLUM_LOADING = null;
function loadPannellum() {
    if (PANNELLUM_LOADING) return PANNELLUM_LOADING;
    if (window.pannellum) return Promise.resolve(window.pannellum);
    PANNELLUM_LOADING = new Promise((resolve, reject) => {
        const cssId = "pannellum-css";
        if (!document.getElementById(cssId)) {
            const l = document.createElement("link");
            l.id = cssId;
            l.rel = "stylesheet";
            l.href = "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css";
            document.head.appendChild(l);
        }
        const jsId = "pannellum-js";
        if (document.getElementById(jsId)) return resolve(window.pannellum);
        const s = document.createElement("script");
        s.id = jsId;
        s.src = "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js";
        s.async = true;
        s.onload = () => resolve(window.pannellum);
        s.onerror = () => reject(new Error("pannellum failed to load"));
        document.body.appendChild(s);
    });
    return PANNELLUM_LOADING;
}

// Convert `/api/uploads/...` to full URL if needed
export function resolveMediaUrl(url) {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
}


// =============================== 360° VIEWER ===============================
export function Panorama360Viewer({ open, url, caption, onClose }) {
    const containerRef = useRef(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!open || !url) return;
        setErr("");
        setLoading(true);
        let viewer = null;
        loadPannellum().then((pn) => {
            if (!containerRef.current) return;
            try {
                viewer = pn.viewer(containerRef.current, {
                    type: "equirectangular",
                    panorama: resolveMediaUrl(url),
                    autoLoad: true,
                    showControls: true,
                    showZoomCtrl: true,
                    hfov: 100,
                });
                setLoading(false);
            } catch (e) {
                setErr("Impossibile aprire il panorama");
                setLoading(false);
            }
        }).catch(() => { setErr("Viewer 360° non disponibile"); setLoading(false); });
        return () => { try { viewer && viewer.destroy && viewer.destroy(); } catch (_) { /* ignore */ } };
    }, [open, url]);

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[70] bg-black/90 flex flex-col" data-testid="panorama-360-modal">
            <div className="p-3 flex items-center justify-between text-white">
                <div className="flex items-center gap-2">
                    <Globe2 size={18} className="text-emerald-400" />
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-emerald-300">Vista 360°</div>
                        <div className="text-sm font-semibold truncate max-w-[70vw]">{caption || "Panorama"}</div>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
                    data-testid="panorama-360-close"
                ><X size={16} /></button>
            </div>
            <div className="flex-1 relative">
                {loading && (
                    <div className="absolute inset-0 flex items-center justify-center text-white/80">
                        <Loader2 size={30} className="animate-spin" />
                    </div>
                )}
                {err && (
                    <div className="absolute inset-0 flex items-center justify-center text-red-300 text-sm">{err}</div>
                )}
                <div ref={containerRef} className="w-full h-full" />
            </div>
        </div>
    );
}


// =============================== READ-ONLY GALLERY ===============================
export function StopMediaGallery({ stop, compact = false, onOpen360, onOpenPhoto }) {
    const photos = stop?.photos || [];
    const photos360 = stop?.photos_360 || [];
    const matterportUrl = stop?.matterport_url || "";
    const virtualTourUrl = stop?.virtual_tour_url || "";
    if (photos.length === 0 && photos360.length === 0 && !matterportUrl && !virtualTourUrl) return null;

    return (
        <div className="mt-2 space-y-2" data-testid="stop-media-gallery">
            {photos.length > 0 && (
                <div className="flex gap-1.5 overflow-x-auto pb-1">
                    {photos.map((p, i) => (
                        <button
                            key={i}
                            onClick={() => onOpenPhoto && onOpenPhoto(p, i)}
                            className="shrink-0 relative rounded-lg overflow-hidden border border-stone-200 hover:border-emerald-500 transition-all group"
                            style={{ width: compact ? 56 : 74, height: compact ? 56 : 74 }}
                            data-testid={`stop-media-photo-${i}`}
                            title={p.caption || `Foto ${i + 1}`}
                        >
                            <img src={resolveMediaUrl(p.url)} alt={p.caption || ""} className="w-full h-full object-cover" loading="lazy" />
                        </button>
                    ))}
                </div>
            )}
            <div className="flex flex-wrap gap-1.5">
                {photos360.map((p, i) => (
                    <button
                        key={i}
                        onClick={() => onOpen360 && onOpen360(p)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-full px-2.5 py-1"
                        data-testid={`stop-media-360-${i}`}
                    >
                        <Globe2 size={11} /> Vista 360°{p.caption ? ` · ${p.caption}` : ""}
                    </button>
                ))}
                {matterportUrl && (
                    <a
                        href={resolveMediaUrl(matterportUrl)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-800 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-full px-2.5 py-1"
                        data-testid="stop-media-matterport"
                    >
                        <HomeIcon size={11} /> Tour Matterport <ExternalLink size={9} />
                    </a>
                )}
                {virtualTourUrl && (
                    <a
                        href={resolveMediaUrl(virtualTourUrl)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-800 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-full px-2.5 py-1"
                        data-testid="stop-media-tour"
                    >
                        <Globe2 size={11} /> Tour virtuale <ExternalLink size={9} />
                    </a>
                )}
            </div>
        </div>
    );
}


// =============================== EDITOR (modal) ===============================
export function StopMediaEditor({ open, stop, onClose, onSave, allowUpload = true, testidPrefix = "stop-media", borgoSlug = "" }) {
    const [photos, setPhotos] = useState([]);
    const [photos360, setPhotos360] = useState([]);
    const [matterportUrl, setMatterportUrl] = useState("");
    const [virtualTourUrl, setVirtualTourUrl] = useState("");
    const [uploading, setUploading] = useState("");
    const [err, setErr] = useState("");
    // AI suggestions
    const [suggestions, setSuggestions] = useState(null);
    const [suggBusy, setSuggBusy] = useState(false);

    useEffect(() => {
        if (open && stop) {
            setPhotos((stop.photos || []).map((p) => ({ ...p })));
            setPhotos360((stop.photos_360 || []).map((p) => ({ ...p })));
            setMatterportUrl(stop.matterport_url || "");
            setVirtualTourUrl(stop.virtual_tour_url || "");
            setErr("");
            setSuggestions(null);
        }
    }, [open, stop]);

    const fetchSuggestions = async () => {
        if (!stop) return;
        setSuggBusy(true);
        try {
            const params = { stop_name: stop.name, borgo: borgoSlug, lang: "it" };
            if (stop.lat) params.lat = stop.lat;
            if (stop.lng) params.lng = stop.lng;
            const { data } = await axios.get(`${API}/itineraries/media-suggestions`, { params });
            setSuggestions(data);
        } catch (e) {
            setErr("Impossibile recuperare i suggerimenti");
        } finally { setSuggBusy(false); }
    };

    const addPhotoUrl = (url) => {
        if (!url) return;
        if (photos.some((p) => p.url === url)) return;
        setPhotos((prev) => [...prev, { url, caption: "", kind: "photo" }]);
    };

    if (!open) return null;

    const uploadFile = async (file, kind) => {
        setUploading(kind);
        setErr("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const client = allowUpload ? adminAxios() : axios.create({ baseURL: API });
            const { data } = await client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            return data.url;
        } catch (e) {
            setErr(e?.response?.data?.detail || "Upload fallito. Puoi incollare un URL esterno.");
            return null;
        } finally { setUploading(""); }
    };

    const addPhoto = async (fileOrUrl, kind = "photo") => {
        let url = "";
        if (typeof fileOrUrl === "string") url = fileOrUrl.trim();
        else url = await uploadFile(fileOrUrl, kind);
        if (!url) return;
        const entry = { url, caption: "", kind };
        if (kind === "photo_360") setPhotos360((p) => [...p, entry]);
        else setPhotos((p) => [...p, entry]);
    };

    const handleSave = () => {
        onSave({
            photos,
            photos_360: photos360,
            matterport_url: matterportUrl.trim(),
            virtual_tour_url: virtualTourUrl.trim(),
        });
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[65] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3" data-testid={`${testidPrefix}-editor`}>
            <div className="bg-white rounded-3xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh]">
                <div className="p-5 border-b border-stone-100 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Media della tappa</div>
                        <h3 className="font-serif text-lg font-semibold">{stop?.name || "Tappa"}</h3>
                    </div>
                    <button onClick={onClose} className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center" data-testid={`${testidPrefix}-close`}><X size={16} /></button>
                </div>
                <div className="p-6 overflow-y-auto flex-1 space-y-6">
                    {err && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{err}</div>}

                    {/* AI Suggestions */}
                    <div className="rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-purple-50/60 p-4" data-testid={`${testidPrefix}-suggestions`}>
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                            <div>
                                <div className="text-sm font-semibold text-indigo-900">🤖 Suggerimenti automatici</div>
                                <div className="text-[11px] text-stone-600 mt-0.5">Wikipedia, Wikimedia Commons, Google Street View, YouTube 360°. Tutti gratuiti, licenza libera.</div>
                            </div>
                            <button
                                onClick={fetchSuggestions}
                                disabled={suggBusy}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold disabled:opacity-50"
                                data-testid={`${testidPrefix}-suggestions-fetch`}
                            >
                                {suggBusy ? <Loader2 size={12} className="animate-spin" /> : <Globe2 size={12} />}
                                {suggestions ? "Aggiorna" : "Cerca suggerimenti"}
                            </button>
                        </div>

                        {suggestions && (
                            <div className="space-y-3">
                                {/* Wikipedia extract */}
                                {suggestions.wikipedia && (
                                    <div className="bg-white rounded-xl border border-indigo-100 p-3 flex gap-3">
                                        {suggestions.wikipedia.thumbnail && (
                                            <img src={suggestions.wikipedia.thumbnail} alt="" className="w-20 h-20 object-cover rounded-lg shrink-0" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-bold">📚 Wikipedia {suggestions.wikipedia.lang?.toUpperCase()}</div>
                                            <a href={suggestions.wikipedia.url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-stone-900 hover:text-indigo-700 inline-flex items-center gap-1">
                                                {suggestions.wikipedia.title} <ExternalLink size={10} />
                                            </a>
                                            <div className="text-xs text-stone-600 mt-1 line-clamp-3">{suggestions.wikipedia.extract}</div>
                                        </div>
                                    </div>
                                )}

                                {/* Image grid */}
                                {(suggestions.images || []).length > 0 && (
                                    <div>
                                        <div className="text-[11px] font-semibold text-stone-700 mb-2 flex items-center justify-between">
                                            <span>🖼️ {suggestions.images.length} immagini da Wikimedia Commons</span>
                                            <button
                                                onClick={() => (suggestions.images || []).forEach((u) => addPhotoUrl(u))}
                                                className="text-[10px] text-indigo-700 hover:underline font-bold"
                                                data-testid={`${testidPrefix}-suggestions-addall`}
                                            >➕ Aggiungi tutte</button>
                                        </div>
                                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                                            {suggestions.images.map((url, i) => {
                                                const already = photos.some((p) => p.url === url);
                                                return (
                                                    <button
                                                        key={i}
                                                        onClick={() => addPhotoUrl(url)}
                                                        disabled={already}
                                                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all group ${already ? "border-emerald-500 opacity-60" : "border-transparent hover:border-indigo-500"}`}
                                                        title={already ? "Già aggiunta" : "Clicca per aggiungere"}
                                                        data-testid={`${testidPrefix}-suggestions-img-${i}`}
                                                    >
                                                        <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                                                        {already && <div className="absolute inset-0 flex items-center justify-center bg-emerald-600/60 text-white text-lg font-bold">✓</div>}
                                                        {!already && <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] font-semibold py-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1"><Plus size={9} /> Aggiungi</div>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Street View + external link buttons */}
                                <div className="flex flex-wrap gap-2 pt-2 border-t border-indigo-100">
                                    {suggestions.street_view_url && (
                                        <button
                                            onClick={() => setVirtualTourUrl(suggestions.street_view_url)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border-2 border-blue-200 hover:border-blue-500 text-blue-800 text-xs font-semibold"
                                            data-testid={`${testidPrefix}-suggestions-streetview`}
                                        >
                                            🗺️ Usa Google Street View come tour virtuale
                                        </button>
                                    )}
                                    {suggestions.youtube_360_url && (
                                        <a
                                            href={suggestions.youtube_360_url}
                                            target="_blank" rel="noreferrer"
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border-2 border-red-200 hover:border-red-500 text-red-800 text-xs font-semibold"
                                            data-testid={`${testidPrefix}-suggestions-yt`}
                                        >
                                            ▶️ Cerca YouTube 360° <ExternalLink size={10} />
                                        </a>
                                    )}
                                    {suggestions.commons_search_url && (
                                        <a
                                            href={suggestions.commons_search_url}
                                            target="_blank" rel="noreferrer"
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border-2 border-purple-200 hover:border-purple-500 text-purple-800 text-xs font-semibold"
                                            data-testid={`${testidPrefix}-suggestions-commons`}
                                        >
                                            📷 Altre foto su Commons <ExternalLink size={10} />
                                        </a>
                                    )}
                                </div>

                                {suggestions.images?.length === 0 && !suggestions.wikipedia && (
                                    <div className="text-xs text-stone-500 py-3 text-center">Nessun risultato trovato. Prova a modificare il nome della tappa in modo più specifico.</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Photos */}
                    <MediaListEditor
                        title="🖼️ Foto"
                        subtitle="Foto normali della tappa (paesaggi, dettagli, cibo…)"
                        items={photos}
                        onChange={setPhotos}
                        onAddFile={allowUpload ? (f) => addPhoto(f, "photo") : null}
                        onAddUrl={(u) => addPhoto(u, "photo")}
                        uploading={uploading === "photo"}
                        testidPrefix={`${testidPrefix}-photos`}
                        thumbnail
                    />

                    {/* 360° */}
                    <MediaListEditor
                        title="🌐 Foto 360°"
                        subtitle="Panorami sferici equirettangolari (formato 2:1). Si aprono nel viewer Pannellum."
                        items={photos360}
                        onChange={setPhotos360}
                        onAddFile={allowUpload ? (f) => addPhoto(f, "photo_360") : null}
                        onAddUrl={(u) => addPhoto(u, "photo_360")}
                        uploading={uploading === "photo_360"}
                        testidPrefix={`${testidPrefix}-360`}
                        thumbnail
                        badge="360°"
                    />

                    {/* Matterport / Virtual tour URLs */}
                    <div className="rounded-2xl border-2 border-purple-200 bg-purple-50/40 p-4">
                        <div className="text-sm font-semibold text-purple-900 mb-2">🏠 Tour virtuali (Matterport / MPSkin)</div>
                        <div className="space-y-2">
                            <div>
                                <label className="text-[11px] font-bold text-stone-600 uppercase tracking-wider block mb-1">Matterport / MPSkin URL</label>
                                <input
                                    value={matterportUrl}
                                    onChange={(e) => setMatterportUrl(e.target.value)}
                                    placeholder="https://my.matterport.com/show/?m=..."
                                    className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm outline-none focus:border-purple-500"
                                    data-testid={`${testidPrefix}-matterport-url`}
                                />
                            </div>
                            <div>
                                <label className="text-[11px] font-bold text-stone-600 uppercase tracking-wider block mb-1">Altro tour virtuale (URL)</label>
                                <input
                                    value={virtualTourUrl}
                                    onChange={(e) => setVirtualTourUrl(e.target.value)}
                                    placeholder="es. Kuula, Google Street View, YouTube 360°…"
                                    className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm outline-none focus:border-purple-500"
                                    data-testid={`${testidPrefix}-tour-url`}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="p-4 border-t border-stone-100 flex items-center justify-between">
                    <button onClick={onClose} className="px-4 py-2 rounded-full bg-stone-100 hover:bg-stone-200 text-sm font-semibold" data-testid={`${testidPrefix}-cancel`}>Annulla</button>
                    <button onClick={handleSave} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold" data-testid={`${testidPrefix}-save`}>
                        <ImageIcon size={13} /> Salva media
                    </button>
                </div>
            </div>
        </div>
    );
}


function MediaListEditor({ title, subtitle, items, onChange, onAddFile, onAddUrl, uploading, testidPrefix, thumbnail = false, badge = null }) {
    const [urlInput, setUrlInput] = useState("");
    const fileInputRef = useRef(null);

    const patch = (idx, k, v) => onChange(items.map((it, i) => i === idx ? { ...it, [k]: v } : it));
    const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

    return (
        <div className="rounded-2xl border-2 border-stone-200 bg-white p-4">
            <div className="mb-2">
                <div className="text-sm font-semibold text-stone-900">{title} <span className="ml-1 text-stone-400 font-normal">({items.length})</span></div>
                {subtitle && <div className="text-[11px] text-stone-500 mt-0.5">{subtitle}</div>}
            </div>
            {items.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                    {items.map((m, i) => (
                        <div key={i} className="flex items-start gap-2 bg-stone-50 rounded-xl p-2 border border-stone-100" data-testid={`${testidPrefix}-item-${i}`}>
                            {thumbnail && m.url && (
                                <div className="relative shrink-0">
                                    <img src={resolveMediaUrl(m.url)} alt="" className="w-14 h-14 rounded object-cover border border-stone-200" />
                                    {badge && <span className="absolute -top-1 -right-1 bg-emerald-600 text-white text-[8px] font-bold px-1 rounded-full">{badge}</span>}
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <input
                                    value={m.caption || ""}
                                    onChange={(e) => patch(i, "caption", e.target.value)}
                                    placeholder="Didascalia (opzionale)"
                                    className="w-full px-2 py-1 border border-stone-200 rounded text-xs outline-none focus:border-emerald-500 mb-1"
                                    data-testid={`${testidPrefix}-caption-${i}`}
                                />
                                <div className="text-[10px] text-stone-500 truncate" title={m.url}>{m.url}</div>
                            </div>
                            <button onClick={() => remove(i)} className="shrink-0 w-7 h-7 rounded-full bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center" data-testid={`${testidPrefix}-remove-${i}`}><Trash2 size={11} /></button>
                        </div>
                    ))}
                </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex-1 min-w-[200px] flex items-center gap-1">
                    <input
                        value={urlInput}
                        onChange={(e) => setUrlInput(e.target.value)}
                        placeholder="URL immagine (https://…)"
                        className="flex-1 px-3 py-1.5 border border-stone-200 rounded-full text-xs outline-none focus:border-emerald-500"
                        data-testid={`${testidPrefix}-url-input`}
                    />
                    <button
                        onClick={() => { if (urlInput.trim()) { onAddUrl(urlInput.trim()); setUrlInput(""); } }}
                        disabled={!urlInput.trim()}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-stone-100 hover:bg-emerald-100 text-stone-700 disabled:opacity-50 text-xs font-semibold"
                        data-testid={`${testidPrefix}-url-add`}
                    ><Plus size={11} /> URL</button>
                </div>
                {onAddFile && (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) { onAddFile(f); e.target.value = ""; } }}
                            data-testid={`${testidPrefix}-file-input`}
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50"
                            data-testid={`${testidPrefix}-file-btn`}
                        >
                            {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Carica
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
